import type { OnDestroy } from '@angular/core';
import { Inject, Injectable } from '@angular/core';
import { filter, map, take, tap } from 'rxjs/operators';
import type { WebSocketSubject } from 'rxjs/webSocket';
import { webSocket } from 'rxjs/webSocket';
import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject, of } from 'rxjs';
import type { UserOrg } from 'src/app/auth.service';
import { AuthService } from 'src/app/auth.service';
import type { PeerMessage } from '../media-streams/peer-messages';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';

@Injectable()
export class WebsocketService implements OnDestroy {

  public messages$ = new Subject<PeerMessage>();

  public connectionClosed$ = new Subject<CloseEvent>();

  public isConnected$ = new BehaviorSubject<boolean>(false);

  protected apiUrlTemplate = `${environment.apiBaseUrl.replace('https', 'wss').replace('http', 'ws')}/core/ws?org.id=:orgId`;

  private socket$?: WebSocketSubject<PeerMessage>;

  private isIntentionallyClosed = false;

  private reconnectAttempts = 0;

  private readonly maxReconnectAttempts = 5;

  private readonly baseReconnectDelay = 1000; // 1 second

  private reconnectTimeout?: ReturnType<typeof setTimeout>;

  private isReconnecting = false;

  private isConnecting = false;

  public constructor(
    @Inject(AuthService) private readonly authService: AuthService,
    @Inject(LoggerService) private readonly logger: LoggerService,
  ) {
    this.logger.debug('log', ['WebSocket', 'Service constructor called - initializing connection']);
    this.connect().subscribe();
  }

  public connect(): Observable<boolean> {
    this.logger.debug('log', ['WebSocket', `connect() called - socket exists: ${Boolean(this.socket$)}, socket closed: ${this.socket$?.closed}`]);

    // If already connected or connecting, return success immediately
    if (this.isConnected$.value || this.isConnecting) {
      this.logger.debug('log', ['WebSocket', 'Already connected or connecting - skipping duplicate connection attempt']);
      return of(true);
    }

    // Clean up the old socket reference if it exists
    if (this.socket$) {
      this.logger.debug('log', ['WebSocket', 'Cleaning up old socket reference before creating new connection']);
      this.socket$ = undefined;
    }

    this.isConnecting = true;

    this.logger.debug('log', ['WebSocket', 'Creating new connection - getting current organization']);
    return this.authService.getCurrentOrganization()
      .pipe(
        tap((userOrg: UserOrg) => {
          this.apiUrlTemplate = this.apiUrlTemplate.replace(':orgId', userOrg.id);

          this.logger.debug('log', ['WebSocket', `Got organization: ${userOrg.id}, - initializing WebSocket`]);
          this.initializeWebSocket(userOrg);
        }),
        tap({
          error: err => {
            this.logger.debug('error', ['WebSocket', 'Failed to get organization:', err]);
            // Reset isConnecting flag so subsequent reconnection attempts can proceed
            this.isConnecting = false;
          },
        }),
        map(() => true),
      );
  }

  public sendMessage(msg: PeerMessage): void {
    this.logger.debug('log', ['WebSocket', `sendMessage() called with message type: ${msg.type}`]);
    this.logger.debug('log', ['WebSocket', 'Message details:', msg]);
    this.logger.debug('log', ['WebSocket', `Socket state - isConnected: ${this.isConnected$.value}, socket$ exists: ${Boolean(this.socket$)}`]);

    // If not connected and not connecting, trigger connection
    if (!this.isConnected$.value && !this.isConnecting) {
      this.logger.debug('log', ['WebSocket', 'Not connected - initiating connection']);
      this.connect().subscribe();
    }

    // Wait for connection to be established before sending
    this.logger.debug('log', ['WebSocket', 'Waiting for connection to be established before sending message']);
    this.isConnected$.pipe(
      filter(isConnected => isConnected), // Only proceed when connected
      take(1), // Take the first true value and complete
    ).subscribe(() => {
      this.logger.debug('log', ['WebSocket', 'Connection established - sending message now']);
      try {
        this.socket$?.next(msg);
        this.logger.debug('log', ['WebSocket', 'Message sent successfully']);
      } catch (err) {
        this.logger.debug('error', ['WebSocket', 'Error sending message:', err]);
      }
    });
  }

  public close(): void {
    this.logger.debug('log', ['WebSocket', 'close() called - intentionally closing connection']);
    this.isIntentionallyClosed = true;
    this.isReconnecting = false;
    this.isConnected$.next(false);
    this.isConnecting = false;

    this.logger.debug('log', ['WebSocket', 'Set isIntentionallyClosed flag to true and reset isReconnecting/isConnected/isConnecting flags']);
    this.clearReconnectTimeout();
    if (this.socket$ && !this.socket$.closed) {
      this.logger.debug('log', ['WebSocket', 'Completing socket connection']);
      this.socket$.complete();
    } else {
      this.logger.debug('log', ['WebSocket', 'Socket already closed or does not exist']);
    }
  }

  public ngOnDestroy(): void {
    this.logger.debug('log', ['WebSocket', 'ngOnDestroy() called - cleaning up service']);
    this.close();
    this.messages$.complete();
    this.connectionClosed$.complete();
    this.isConnected$.complete();
  }

  private initializeWebSocket(userOrg: UserOrg): void {
    this.logger.debug('log', ['WebSocket', `initializeWebSocket() called for org: ${userOrg.id}`]);

    // Reset intentional close flag when creating a new connection
    this.isIntentionallyClosed = false;

    this.logger.debug('log', ['WebSocket', 'Reset isIntentionallyClosed flag to false']);

    const token = this.authService.getOauthAccessToken();

    // Create WebSocket connection
    const wsUrl = this.apiUrlTemplate;

    this.logger.debug('log', ['WebSocket', `Creating new WebSocket connection to: ${wsUrl.substring(0, wsUrl.indexOf('?'))}`]);
    this.socket$ = this.getNewWebSocket(wsUrl, token);

    // Subscribe to incoming messages
    this.logger.debug('log', ['WebSocket', 'Subscribing to WebSocket events']);
    this.socket$.subscribe({
      next: (msg: PeerMessage) => {
        this.logger.debug('log', ['WebSocket', `Received message: ${msg.type}`]);
        this.messages$.next(msg);
      },
      error: (err: any) => {
        this.isConnected$.next(false);
        this.isConnecting = false;

        this.logger.debug('error', ['WebSocket', 'Error event triggered:', err]);
        this.logger.debug('error', ['WebSocket', `Error details: ${JSON.stringify(err)}`]);
        this.logger.debug('error', ['WebSocket', `Current state - isIntentionallyClosed: ${this.isIntentionallyClosed}, reconnectAttempts: ${this.reconnectAttempts}, isReconnecting: ${this.isReconnecting}, isConnected: ${this.isConnected$.value}`]);
        // Error event is fired first, followed by complete. Only handle reconnect in complete to avoid duplicate attempts.
        this.logger.debug('log', ['WebSocket', 'Error event - waiting for complete event to handle reconnection']);
      },
      complete: () => {
        this.isConnected$.next(false);
        this.isConnecting = false;

        this.logger.debug('warn', ['WebSocket', 'Complete event triggered - connection closed']);
        this.logger.debug('warn', ['WebSocket', `Current state - isIntentionallyClosed: ${this.isIntentionallyClosed}, reconnectAttempts: ${this.reconnectAttempts}, isReconnecting: ${this.isReconnecting}, isConnected: ${this.isConnected$.value}`]);
        // Attempt to reconnect on unexpected closure
        this.attemptReconnect();
      },
    });

    // Auth message will be sent automatically in openObserver after connection is established
    this.logger.debug('log', ['WebSocket', 'Auth message will be sent once connection opens']);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      this.logger.debug('log', ['WebSocket', 'Clearing reconnect timeout']);
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
  }


  private attemptReconnect(): void {
    this.logger.debug('log', ['WebSocket', 'attemptReconnect() called']);
    this.logger.debug('log', ['WebSocket', `Reconnect state - isIntentionallyClosed: ${this.isIntentionallyClosed}, reconnectAttempts: ${this.reconnectAttempts}, maxAttempts: ${this.maxReconnectAttempts}, isReconnecting: ${this.isReconnecting}`]);

    if (this.isIntentionallyClosed) {
      this.logger.debug('log', ['WebSocket', 'Connection was intentionally closed, not reconnecting']);
      return;
    }

    if (this.isReconnecting) {
      this.logger.debug('log', ['WebSocket', 'Reconnection already in progress, ignoring duplicate attempt']);
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.logger.debug('error', ['WebSocket', `Max reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`]);
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;
    const delay = this.baseReconnectDelay * (2 ** (this.reconnectAttempts - 1));

    this.logger.debug('log', ['WebSocket', `Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`]);

    this.reconnectTimeout = setTimeout(() => {
      this.logger.debug('log', ['WebSocket', 'Reconnect timeout fired - initiating connection']);
      // Reset isReconnecting so that if this attempt fails, closeObserver can trigger another attempt
      this.isReconnecting = false;
      this.connect().subscribe({
        next: success => {
          this.logger.debug('log', ['WebSocket', `Reconnect subscribe next() called - success: ${success}`]);
          // Note: Don't reset reconnectAttempts here - it will be reset in openObserver when connection actually succeeds
        },
        error: (err: any) => {
          this.logger.debug('error', ['WebSocket', 'Reconnection subscribe error() called:', err]);
          this.logger.debug('error', ['WebSocket', 'Reconnection failed - will attempt again']);
          this.attemptReconnect(); // Try again
        },
      });
    }, delay);

    this.logger.debug('log', ['WebSocket', 'Reconnect timeout scheduled with ID:', this.reconnectTimeout]);
  }

  private getNewWebSocket(url: string, token: string): WebSocketSubject<PeerMessage> {
    this.logger.debug('log', ['WebSocket', 'getNewWebSocket() called - creating WebSocketSubject']);

    return webSocket({
      url,
      protocol: [`access_token.${token}`],
      // Custom serializer to ensure proper JSON handling
      serializer: (msg: PeerMessage) => {
        const serialized = JSON.stringify(msg);
        this.logger.debug('log', ['WebSocket', 'Serializing message:', serialized]);
        return serialized;
      },
      openObserver: {
        next: () => {
          this.isConnected$.next(true);
          this.isConnecting = false;

          this.logger.debug('log', ['WebSocket', 'openObserver - WebSocket connection opened successfully']);

          // Reset reconnection state now that we're successfully connected
          if (this.reconnectAttempts > 0 || this.isReconnecting) {
            this.logger.debug('log', ['WebSocket', 'Connection established after reconnection - resetting reconnect attempts and flags']);
            this.reconnectAttempts = 0;
            this.isReconnecting = false;
          }
        },
      },
      closeObserver: {
        next: (closeEvent: CloseEvent) => {
          this.isConnected$.next(false);
          this.isConnecting = false;

          this.logger.debug('error', [
            'WebSocket',
            'closeObserver - WebSocket closed event:',
            {
              code: closeEvent.code,
              reason: closeEvent.reason,
              wasClean: closeEvent.wasClean,
              timestamp: new Date().toISOString(),
            },
          ]);

          /*
           * Common close codes:
           * 1000: Normal closure
           * 1006: Abnormal closure (connection lost, no close frame)
           * 1008: Policy violation (server rejected message/connection)
           * 1011: Server error
           */

          if (closeEvent.code === 1006) {
            this.logger.debug('error', ['WebSocket', 'Abnormal closure detected (1006). Possible causes:']);
            this.logger.debug('error', ['WebSocket', '- Server rejected the message format']);
            this.logger.debug('error', ['WebSocket', '- Network connectivity issue']);
            this.logger.debug('error', ['WebSocket', '- Server crashed or was terminated']);
          } else if (closeEvent.code === 1008) {
            this.logger.debug('error', ['WebSocket', 'Server rejected the connection/message (policy violation - 1008)']);
          } else if (closeEvent.code === 1000) {
            this.logger.debug('log', ['WebSocket', 'Normal closure (1000)']);
          }

          // Notify all subscribers that the connection has closed
          this.logger.debug('log', ['WebSocket', 'Notifying connectionClosed$ subscribers']);
          this.connectionClosed$.next(closeEvent);

          // Attempt to reconnect when the connection closes unexpectedly
          this.logger.debug('log', ['WebSocket', 'closeObserver - triggering reconnection attempt']);
          this.attemptReconnect();
        },
      },
    });
  }

}
